import React, { useState } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import { getOverlayPortalParent, TagColored, TooltipTrigger, useReadOnly } from '@spark';
import { NONE_SELECTED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import { getSparkTagVariant, SPARK_TAG_VARIANT_COLORS } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { getLabelConfidenceTooltip } from '@/features/tasks/utils/labelConfidenceTooltip';

const EllipsisTagWithTooltip = ({ text, maxWidth = 300, color, style = {}, zIndex = 4000, noMargin = false, closable, onClose, ...props }) => {
    const [isTruncated, setIsTruncated] = React.useState(false);
    const ref = React.useRef(null);
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [text, maxWidth, style]);
    const tag = (
        <Tag ref={ref} color={color} closable={closable} onClose={onClose} {...props} style={{ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle', marginBottom: noMargin ? 0 : '5px', ...style }}>
            {text}
        </Tag>
    );
    return isTruncated ? <Tooltip title={text} placement="top" zIndex={zIndex} getPopupContainer={() => document.body}>{tag}</Tooltip> : tag;
};

const EditableContainer = ({ children, onEdit, isEditing, scrollWhenNotEditing = false, maxHeightWhenNotEditing = 200, className = '', style = {} }) => {
    return (
        <div
            className={`${className} ${!isEditing && scrollWhenNotEditing ? 'optune-scrollbar' : ''}`}
            onClick={onEdit}
            style={{
                boxSizing: 'border-box', width: '100%', maxWidth: '100%', minWidth: 0,
                cursor: isEditing ? 'default' : 'pointer', padding: '4px', display: 'flex',
                flexWrap: 'wrap', gap: '6px', border: '1px dashed transparent', borderRadius: '4px',
                transition: 'all 0.2s ease', position: 'relative', minHeight: '32px',
                background: isEditing ? 'transparent' : 'rgba(255, 255, 255, 0.03)',
                alignItems: 'center', paddingRight: '40px',
                overflowY: !isEditing && scrollWhenNotEditing ? 'auto' : undefined,
                maxHeight: !isEditing && scrollWhenNotEditing ? maxHeightWhenNotEditing : undefined,
                ...style,
            }}
            onMouseOver={!isEditing ? (e) => { e.currentTarget.style.border = '1px dashed #1890ff'; } : undefined}
            onMouseOut={!isEditing ? (e) => { e.currentTarget.style.border = '1px dashed transparent'; } : undefined}
        >
            <div style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>{children}</div>
        </div>
    );
};


const EditableLabels = ({
    labels,
    onSave,
    allLabelOptions,
    record,
    showSparkleInTag = false,
    style = {},
    emptyValue = NONE_SELECTED,
    onDropdownVisibleChange,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedLabels, setSelectedLabels] = useState(labels || []);
    const { readOnly } = useReadOnly();
    const allLabelOptionsWithEmpty = allLabelOptions?.concat(emptyValue).map(label => ({
        label: label,
        value: label
    }))

    // Same color mapping as the Spark TagColored surfaces (Data Editor labels
    // column, Model Responses "True Labels" view mode): compute the variant
    // from the plain label list, without the appended empty value, so a label
    // gets the identical color everywhere.
    const sparkTagStyle = (label) => {
        const variant = label === emptyValue ? 'disabled' : getSparkTagVariant(label, allLabelOptions || []);
        const { bg, color } = SPARK_TAG_VARIANT_COLORS[variant] || SPARK_TAG_VARIANT_COLORS.disabled;
        return { background: bg, color, borderColor: 'transparent' };
    };

    // Handle selection change
    const handleChange = async (values) => {
        let sortedValues = values.sort();
        if (sortedValues.length > 1) {
            if (!selectedLabels.includes(emptyValue) && sortedValues.includes(emptyValue)) {
                sortedValues = [emptyValue]
            } else {
                sortedValues = sortedValues.filter(label => label !== emptyValue)
            }
        }
        else if (sortedValues.length === 0) {
            sortedValues = [emptyValue]
        }
        setSelectedLabels(sortedValues);

        try {
            // If onSave returns a promise, wait for it
            const result = onSave(sortedValues.map(label => label === emptyValue ? "" : label));
            if (result && typeof result.then === 'function') {
                await result;
            }
            setIsEditing(false);
        } catch (error) {
            // If save fails, reset the selected labels back to original
            setSelectedLabels(labels || []);
            // Keep editing mode active so user can try again
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    // Handle keyboard events
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsEditing(false);
            e.preventDefault();
        }
    };

    const showPlaceholder = (selectedLabels?.length || 0) === 0;
    const effectiveLabels = (selectedLabels || []);

    const renderViewTag = (label, idx) => {
        const withSparkle = showSparkleInTag && label !== emptyValue;
        const tooltip = showSparkleInTag
            ? getLabelConfidenceTooltip({
                record,
                isAiGenerated: true,
                labelName: label,
                allLabelOptions: allLabelOptions || [],
                isMultilabel: true,
            })
            : '';
        const tag = (
            <TagColored
                variant={label === emptyValue ? 'disabled' : getSparkTagVariant(label, allLabelOptions || [])}
                label={label}
                endIconSlug={withSparkle ? 'sparkles-2' : undefined}
            />
        );
        if (!tooltip) {
            return <React.Fragment key={`${label}-${idx}`}>{tag}</React.Fragment>;
        }
        return (
            <TooltipTrigger key={`${label}-${idx}`} text={tooltip} placement="top">
                {tag}
            </TooltipTrigger>
        );
    };

    return (
        <div style={{ minHeight: '32px', maxWidth: '190px', ...style }}>
            {!isEditing ? (
                <EditableContainer onEdit={readOnly ? undefined : handleEdit} isEditing={isEditing}>
                    {showPlaceholder ? (
                        <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>Click to select labels</span>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {effectiveLabels.map((label, idx) => renderViewTag(label, idx))}
                        </div>
                    )}
                </EditableContainer>
            ) : (
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select labels"
                    value={selectedLabels}
                    disabled={readOnly}
                    onChange={handleChange}
                    onBlur={() => setIsEditing(false)}
                    autoFocus
                    options={allLabelOptionsWithEmpty.map(option => ({
                        label: (
                            <EllipsisTagWithTooltip
                                style={{ marginTop: '5px', marginRight: '5px', ...sparkTagStyle(option.value) }}
                                text={option.label}
                                maxWidth={140}
                                noMargin
                            />
                        ),
                        value: option.value
                    }))}
                    required
                    rules={[{ required: true, message: 'At least one label is required' }]}
                    dropdownStyle={{
                        borderColor: '#e8e8e8'
                    }}
                    getPopupContainer={(trigger) => getOverlayPortalParent(trigger)}
                    onDropdownVisibleChange={onDropdownVisibleChange}
                    onKeyDown={handleKeyDown}
                    tagRender={(props) => {
                        const { value, closable, onClose } = props;
                        return (
                            <EllipsisTagWithTooltip
                                text={value}
                                closable={closable}
                                onClose={onClose}
                                style={{ marginRight: 3, ...sparkTagStyle(value) }}
                                maxWidth={140}
                                noMargin
                            />
                        );
                    }}
                />
            )}
        </div>
    );
};


export default EditableLabels;